import { useEffect } from "react";
import { Link } from "react-router-dom";
import HeroCarousel from "@/pages/home/components/HeroCarousel";
import CategoryGrid from "@/pages/home/components/CategoryGrid";
import SectionHeading from "@/components/base/SectionHeading";
import WorkCard from "@/components/base/WorkCard";
import { works } from "@/mocks/works";
import { formatCount, getCategoryById } from "@/utils/gallery";

const latestWorks = [...works]
  .sort((a, b) => b.date.localeCompare(a.date))
  .slice(0, 8);

const hotWorks = [...works].sort((a, b) => b.views - a.views).slice(0, 5);

const stats = [
  { value: "18", label: "收录作品组", suffix: "+" },
  { value: "5", label: "写真分类", suffix: "" },
  { value: "12.4", label: "月度浏览", suffix: "万" },
  { value: "10", label: "合作摄影师", suffix: "+" },
];

export default function Home() {
  useEffect(() => {
    document.title = "国模白露模特写真作品集，优质摄影图片欣赏平台";
    const description = document.querySelector('meta[name="description"]');
    if (description) {
      description.setAttribute(
        "content",
        "国模白露专注于提供高质量模特摄影作品，收录丰富的写真图片，满足摄影爱好者和模特粉丝的欣赏需求。浏览国模白露最新作品，感受专业摄影风采。"
      );
    }
    const keywords = document.querySelector('meta[name="keywords"]');
    if (keywords) {
      keywords.setAttribute(
        "content",
        "国模白露,模特写真,摄影作品,写真图片,模特图库,写真欣赏,摄影爱好者,国模写真"
      );
    }
    window.scrollTo({ top: 0 });
  }, []);

  return (
    <div className="w-full">
      <HeroCarousel />

      <section className="px-4 py-16 md:px-8 md:py-24">
        <SectionHeading
          eyebrow="CATEGORIES"
          title="按分类探索写真作品"
          description="从时尚大片到复古胶片，国模白露按照拍摄风格与场景整理收录作品，帮助你快速找到感兴趣的写真主题。"
        />
        <CategoryGrid />
      </section>

      <section className="bg-background-100 px-4 py-16 md:px-8 md:py-24">
        <SectionHeading
          eyebrow="LATEST WORKS"
          title="最新上传作品"
          description="持续更新的写真作品集，随时浏览最新拍摄的模特写真与摄影创作。"
          moreHref="/list/fashion"
          moreLabel="浏览全部作品"
        />
        <div className="grid grid-cols-1 gap-5 sm:grid-cols-2 lg:grid-cols-4">
          {latestWorks.map((work, index) => (
            <WorkCard key={work.id} work={work} index={index} />
          ))}
        </div>
      </section>

      <section className="px-4 py-16 md:px-8 md:py-24">
        <SectionHeading
          eyebrow="POPULAR"
          title="热门作品精选"
          description="根据浏览量与收藏数排行，为你呈现近期最受欢迎的模特写真作品。"
        />
        <div className="space-y-4">
          {hotWorks.map((work, index) => {
            const category = getCategoryById(work.categoryId);
            return (
              <Link
                key={work.id}
                to={`/detail/${work.id}`}
                className="group flex flex-col gap-4 rounded-lg border border-background-200 bg-background-50 p-4 transition-all duration-300 hover:border-primary-300 sm:flex-row sm:items-center"
              >
                <div className="flex items-center gap-4">
                  <span className={`w-8 text-center text-2xl font-bold ${index < 3 ? "text-primary-500" : "text-foreground-300"}`}>
                    {String(index + 1).padStart(2, "0")}
                  </span>
                  <div className="relative h-24 w-[72px] shrink-0 overflow-hidden rounded-md bg-background-100 sm:h-28 sm:w-[84px]">
                    <img
                      src={work.cover}
                      alt={`${work.title} - 热门写真作品封面`}
                      title={`${work.title} - 热门写真`}
                      className="h-full w-full object-cover object-top transition-transform duration-500 group-hover:scale-105"
                    />
                  </div>
                </div>
                <div className="flex-1">
                  <div className="flex flex-wrap items-center gap-2">
                    <h3 className="text-base font-semibold text-foreground-950 transition-colors group-hover:text-primary-600">
                      {work.title}
                    </h3>
                    {category && (
                      <span className="rounded-full bg-secondary-100 px-2.5 py-0.5 text-xs text-secondary-900">
                        {category.name}
                      </span>
                    )}
                  </div>
                  <p className="mt-1 line-clamp-1 text-sm text-foreground-500">{work.description}</p>
                  <div className="mt-2 flex flex-wrap items-center gap-x-4 gap-y-1 text-xs text-foreground-500">
                    <span>模特 {work.model}</span>
                    <span className="flex items-center gap-1">
                      <i className="ri-camera-lens-line" />
                      {work.photographer}
                    </span>
                    <span>{work.date}</span>
                  </div>
                </div>
                <div className="flex items-center gap-6 text-sm text-foreground-600 sm:flex-col sm:items-end sm:gap-2">
                  <span className="flex items-center gap-1">
                    <i className="ri-eye-line text-primary-500" />
                    {formatCount(work.views)}
                  </span>
                  <span className="flex items-center gap-1">
                    <i className="ri-heart-3-line text-accent-500" />
                    {formatCount(work.likes)}
                  </span>
                </div>
              </Link>
            );
          })}
        </div>
      </section>

      <section className="bg-background-100 px-4 py-16 md:px-8 md:py-24">
        <div className="grid grid-cols-1 gap-10 lg:grid-cols-2 lg:items-center">
          <div>
            <p className="mb-2 flex items-center gap-2 text-xs font-medium tracking-[0.3em] text-primary-600">
              <span className="h-px w-6 bg-primary-500" />
              ABOUT BAILU
            </p>
            <h2 className="text-2xl font-bold text-foreground-950 md:text-3xl">
              关于国模白露
            </h2>
            <p className="mt-4 text-sm leading-relaxed text-foreground-600">
              国模白露是一个专注于高质量模特写真摄影作品展示与欣赏的图库平台。我们与多位摄影师与模特合作，
              持续收录不同风格与场景的写真作品，涵盖时尚大片、人像写真、户外旅拍、棚拍艺术与复古胶片等主题。
            </p>
            <p className="mt-3 text-sm leading-relaxed text-foreground-600">
              无论你是摄影爱好者、模特追随者，还是写真行业从业者，都可以在这里浏览、筛选并收藏喜欢的作品，
              感受专业摄影带来的视觉魅力。
            </p>
            <div className="mt-8 flex flex-wrap gap-3">
              <Link
                to="/about"
                className="flex items-center gap-2 whitespace-nowrap rounded-md bg-primary-500 px-6 py-3 text-sm font-medium text-background-50 transition-colors hover:bg-primary-600"
              >
                <i className="ri-information-line" />
                了解更多
              </Link>
              <Link
                to="/favorites"
                className="flex items-center gap-2 whitespace-nowrap rounded-md border border-background-300 px-6 py-3 text-sm font-medium text-foreground-800 transition-colors hover:border-primary-400 hover:text-primary-600"
              >
                <i className="ri-heart-3-line" />
                我的收藏
              </Link>
            </div>
          </div>
          <div className="grid grid-cols-2 gap-4">
            {stats.map((stat) => (
              <div key={stat.label} className="rounded-lg border border-background-200 bg-background-50 p-5 md:p-6">
                <p className="text-3xl font-bold text-foreground-950">
                  {stat.value}
                  <span className="text-base font-medium text-primary-600">{stat.suffix}</span>
                </p>
                <p className="mt-1 text-sm text-foreground-500">{stat.label}</p>
              </div>
            ))}
          </div>
        </div>
      </section>
    </div>
  );
}